import type { ComponentProps } from 'react';
import { Box, BoxProps } from 'reflexbox';
import styled, { type DefaultTheme, css } from 'styled-components';

import { Breakpoint } from '../helpers';
import { tokens } from '../theme/tokens';
import { Skeleton } from './Skeleton';

const ParagraphSkeleton = styled(Skeleton.DynamicBox)<{ lines?: number }>`
	height: ${(props) => (props.lines ?? 2) * 24}px;
`;

const lineClamp = css<{ lineClamp?: number }>`
	${(props) =>
		props.lineClamp &&
		css`
			-webkit-line-clamp: ${props.lineClamp};
			-webkit-box-orient: vertical;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
		`}
`;

export const Paragraph = Skeleton(
	styled(Box)<{
		color?: string;
		small?: boolean;
		large?: boolean;
		whiteSpace?: 'pre-line' | 'pre-wrap';
		textDecoration?: 'underline' | 'line-through';
		fontWeight?: 'bold' | 'medium';
		lineClamp?: number;
	}>`
		color: ${(props) =>
			props.color
				? (props.theme.colors[props.color] ?? tokens.text.secondary)
				: tokens.text.secondary};
		font-size: ${(props) =>
			props.small
				? tokens.typography.sizes.small
				: props.large
					? tokens.typography.sizes.h3
					: tokens.typography.sizes.body}px;
		line-height: ${(props) =>
			props.small ? tokens.typography.lineHeights.small : tokens.typography.lineHeights.body};
		letter-spacing: 0.01em;
		font-family: ${tokens.typography.fontFamily};
		white-space: ${(props) => props.whiteSpace ?? 'normal'};
		text-decoration: ${(props) => props.textDecoration ?? 'none'};
		font-weight: ${(props) =>
			props.fontWeight === 'medium'
				? tokens.typography.weights.medium
				: props.fontWeight === 'bold'
					? tokens.typography.weights.bold
					: 450};
		a {
			color: inherit;

			:hover {
				text-decoration: none;
			}
		}

		${lineClamp}
	`,
	(props: ComponentProps<typeof ParagraphSkeleton> & { lines?: number }) => (
		<ParagraphSkeleton {...props} />
	),
);

const linkColor = (color: string | undefined, theme: DefaultTheme) => {
	if (!color) return tokens.text.link;
	if (color in tokens.text) return tokens.text[color as keyof typeof tokens.text];
	if (color in theme.colors) return theme.colors[color as keyof typeof theme.colors];
	return tokens.text.link;
};

export const UnderlineLink = styled.a<{ color?: string; small?: boolean }>`
	appearance: none;
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	color: ${(props) => linkColor(props.color, props.theme)} !important;
	font-size: ${(props) =>
		props.small ? tokens.typography.sizes.small : tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${(props) =>
		props.small ? tokens.typography.lineHeights.small : tokens.typography.lineHeights.body};
	letter-spacing: 0.01em;
	font-family: ${tokens.typography.fontFamily};
	text-align: inherit;
	text-decoration: underline !important;
	text-underline-position: from-font;
	cursor: pointer;

	&:hover {
		color: ${tokens.text.primary} !important;
	}
`;
export const Ellipsis = styled(Box)`
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
`;

const TitleSkeleton = styled(Skeleton.DynamicBox)`
	height: 40px;
`;

export const Title = Skeleton(
	styled(Box)`
		color: ${tokens.text.primary};
		font-size: ${tokens.typography.sizes.title}px;
		font-weight: 600;
		line-height: ${tokens.typography.lineHeights.heading};
		letter-spacing: -0.005em;
		font-family: ${tokens.typography.fontFamily};

		@media screen and (max-width: ${Breakpoint.Medium}) {
			font-size: ${tokens.typography.sizes.title - 6}px;
		}
	`,
	(props: BoxProps) => <TitleSkeleton {...props} />,
);

const HeadingSkeleton = styled(Skeleton.DynamicBox)`
	height: 30px;
`;

export const Heading = Skeleton(
	styled(Box)<{ lineClamp?: number }>`
		color: ${(props) =>
			props.color ? (props.theme.colors[props.color] ?? tokens.text.primary) : tokens.text.primary};
		font-size: ${tokens.typography.sizes.h1}px;
		font-weight: ${tokens.typography.weights.medium};
		line-height: ${tokens.typography.lineHeights.heading};
		letter-spacing: 0.01em;
		font-family: ${tokens.typography.fontFamily};

		${lineClamp}
	`,
	(props: BoxProps) => <HeadingSkeleton {...props} />,
);

const SubheadingSkeleton = styled(Skeleton.DynamicBox)`
	height: 23px;
`;

export const Subheading = Skeleton(
	styled(Box)<{ lineClamp?: number; lineHeight?: number }>`
		color: ${tokens.text.primary};
		font-size: ${tokens.typography.sizes.h2}px;
		font-weight: ${tokens.typography.weights.medium};
		line-height: ${(props) =>
			props.lineHeight != null ? `${props.lineHeight}px` : tokens.typography.lineHeights.heading};
		font-family: ${tokens.typography.fontFamily};

		${lineClamp}
	`,
	(props: BoxProps) => <SubheadingSkeleton {...props} />,
);

const Heading3Skeleton = styled(Skeleton.DynamicBox)`
	height: 20px;
`;

export const Heading3 = Skeleton(
	styled(Box)<{ lineClamp?: number }>`
		color: ${tokens.text.primary};
		font-size: ${tokens.typography.sizes.h3}px;
		font-weight: ${tokens.typography.weights.medium};
		line-height: ${tokens.typography.lineHeights.heading};
		font-family: ${tokens.typography.fontFamily};

		${lineClamp}
	`,
	(props: BoxProps) => <Heading3Skeleton {...props} />,
);

const Heading4Skeleton = styled(Skeleton.DynamicBox)`
	height: 18px;
`;

export const Heading4 = Skeleton(
	styled(Box)<{ lineClamp?: number }>`
		color: ${tokens.text.secondary};
		font-size: ${tokens.typography.sizes.h4}px;
		font-weight: ${tokens.typography.weights.bold};
		line-height: ${tokens.typography.lineHeights.heading};
		letter-spacing: 0.05em;
		text-transform: uppercase;
		font-family: ${tokens.typography.fontFamily};

		${lineClamp}
	`,
	(props: BoxProps) => <Heading4Skeleton {...props} />,
);

export const Label = Skeleton(
	styled(Box)`
		color: ${tokens.text.primary};
		font-size: ${tokens.typography.sizes.label}px;
		font-weight: ${tokens.typography.weights.medium};
		line-height: ${tokens.typography.lineHeights.heading};
		letter-spacing: 0.01em;
		font-family: ${tokens.typography.fontFamily};
	`,
	styled(Skeleton.DynamicBox)`
		height: 18px;
	`,
);

export const LabelPlus = Skeleton(
	styled(Box)`
		color: ${tokens.text.primary};
		font-size: ${tokens.typography.sizes.label}px;
		font-weight: 600;
		line-height: ${tokens.typography.lineHeights.heading};
		letter-spacing: 0.01em;
		font-family: ${tokens.typography.fontFamily};
	`,
	styled(Skeleton.DynamicBox)`
		height: 18px;
	`,
);

const listStyles = css<{ small?: boolean }>`
	margin: ${(props) => (props.small ? tokens.gap.element : tokens.gap.group)}px 0 0;
	padding: 0;
	color: ${tokens.text.secondary};
	font-size: ${(props) =>
		props.small ? tokens.typography.sizes.small : tokens.typography.sizes.body}px;
	font-weight: 450;
	line-height: ${(props) =>
		props.small ? tokens.typography.lineHeights.small : tokens.typography.lineHeights.body};
	letter-spacing: 0.01em;
	font-family: ${tokens.typography.fontFamily};

	li {
		margin-left: 1.5em;
		padding: 0;
	}

	li:not(:last-child) {
		margin-bottom: ${(props) => (props.small ? tokens.gap.inline : tokens.gap.element)}px;
	}
`;

export const UnorderedList = styled.ul<{ small?: boolean }>`
	${listStyles}
`;

export const OrderedList = styled.ol<{ small?: boolean }>`
	${listStyles}
`;
